import {
  ChevronDownIcon,
  KeyboardIcon,
  LogOutIcon,
  RefreshCwIcon,
  SettingsIcon,
  SunMoonIcon,
} from "lucide-react"
import { Link } from "react-router"
import {
  Avatar,
  colourSchemes,
  Disc,
  Kbd,
  RoleGate,
  useColourScheme,
} from "@/design-system/index.ts"
import { installIcon, useInstallOffer } from "@/installed-app/install-offer.tsx"
import { reloadToUpdate, useUpdate } from "@/installed-app/updates.ts"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/primitives/dropdown-menu.tsx"
import { SidebarMenuButton } from "@/primitives/sidebar.tsx"
import type { Me } from "@/sign-in/client.ts"
import { describeStanding } from "./describe.ts"
import { adminDestinations, destinations } from "./destinations.ts"

/*
 * Everything about the signed-in Member and their own browser, behind the sidebar's workspace row:
 * who they are, the pages they set up once rather than work in (My Connectors, Connect an agent),
 * the colour scheme, the keyboard shortcuts, installing Beacon where the browser can, the way into
 * Administration for an Admin, and signing out. While a new release of Beacon waits, "Update
 * Beacon" leads it. Before the shell had it, signing out was a button on Home and the colour scheme
 * lived only on the kitchen sink.
 */

/**
 * The Member's own row (their photo or initials, and their name) and the menu it opens. Collapsed to
 * icons, the row is their disc alone.
 */
export function AccountMenu({
  me,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const { scheme, chooseScheme } = useColourScheme()
  const update = useUpdate().state.status !== "current"
  const { offered, install } = useInstallOffer()
  const InstallIcon = installIcon(false)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <SidebarMenuButton
            size="lg"
            aria-label={`${me.name}, account menu`}
            // The chevron is 14px, not the row's 16px icons.
            className="w-auto min-w-0 flex-1 gap-2 text-foreground [&_svg]:size-3.5"
          />
        }
      >
        <Disc person={me} size={20} />
        <span className="truncate text-ui font-semibold group-data-[collapsible=icon]:hidden">
          {me.name}
        </span>
        <ChevronDownIcon className="size-3.5 shrink-0 text-subtle-foreground group-data-[collapsible=icon]:hidden" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-64">
        <div className="flex gap-2.5 px-2 py-2">
          <Avatar person={me} size={32} />
          <div className="min-w-0">
            <p className="truncate text-ui font-medium">{me.name}</p>
            <p className="truncate text-xs text-muted-foreground">{describeStanding(me)}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        {update && (
          <>
            <DropdownMenuItem onClick={reloadToUpdate}>
              <RefreshCwIcon />
              Update Beacon
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuGroup>
          {destinations
            .filter((destination) => destination.accountMenu)
            .map(({ to, title, Icon, chord }) => (
              <DropdownMenuItem key={to} render={<Link to={to} />}>
                <Icon />
                {title}
                <span className="ml-auto">
                  <Kbd shortcut={chord} />
                </span>
              </DropdownMenuItem>
            ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoonIcon />
              Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={scheme} onValueChange={chooseScheme}>
                {colourSchemes.map(({ value, label, Icon }) => (
                  <DropdownMenuRadioItem key={value} value={value}>
                    <Icon />
                    {label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={onShowShortcuts}>
            <KeyboardIcon />
            Keyboard shortcuts
            <span className="ml-auto">
              <Kbd shortcut="shortcuts" />
            </span>
          </DropdownMenuItem>
          {offered && (
            <DropdownMenuItem onClick={install}>
              <InstallIcon />
              Install Beacon
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        {/* Administration opens on its first page, with the sidebar turned to the Admin's pages. */}
        <RoleGate role={me.role} admits="admin">
          <DropdownMenuItem render={<Link to={adminDestinations[0]?.to ?? "/"} />}>
            <SettingsIcon />
            Administration
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </RoleGate>
        <DropdownMenuItem onClick={onSignOut}>
          <LogOutIcon />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
